import { useGetPortfolios } from "api/portfolios/service";
import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import Select from "components/Select";
import DollarInput from "../../../components/DollarInput";
import PercentageInput from "../../../components/PercentageInput";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2FormPageDarkSelectStyles } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const Lenders = ({ formData = {}, handleInputChange, activeColor, isTheme2 = false }) => {
  const { data: portfolios, isLoading } = useGetPortfolios();
  const [isLightTheme, setIsLightTheme] = useState(false);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = useMemo(
    () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
    [isTheme2]
  );
  const sectionHeadingStyle = useMemo(
    () => ({
      borderBottom: isLightTheme
        ? "1px solid rgba(0, 0, 0, 0.1)"
        : "1px solid rgba(255, 255, 255, 0.1)",
    }),
    [isLightTheme]
  );

  // Get lender data from formData.lender or formData
  const lenderData = formData.lender_defaults || formData;

  // Tema kontrolü
  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Portfolio options
  const portfolioOptions = useMemo(() => {
    if (!portfolios?.results) return [];
    return portfolios.results.map(portfolio => ({
      value: portfolio.documentId,
      label: portfolio.name
    }));
  }, [portfolios]);


  const selectedPortfolio = useMemo(() => {
    if (!lenderData.loans_booked_portfolio) return null;
    return portfolioOptions.find(opt => opt.value === lenderData.loans_booked_portfolio);
  }, [lenderData.loans_booked_portfolio, portfolioOptions]);

  // Portfolio değişikliği
  const handlePortfolioChange = (selectedOption) => {
    const event = {
      target: {
        name: 'lender_defaults.loans_booked_portfolio',
        value: selectedOption ? selectedOption.value : ''
      }
    };
    handleInputChange(event);
  };

  // Generic select change handler
  const handleSelectChange = (name) => (selectedOption) => {
    const event = {
      target: {
        name: `lender_defaults.${name}`,
        value: selectedOption ? selectedOption.value : ''
      }
    };
    handleInputChange(event);
  };

  // Handle nested lender input changes
  const handleLenderInputChange = (e) => {
    const { name, value } = e.target;
    const event = {
      target: {
        name: `lender_defaults.${name}`,
        value: value
      }
    };
    handleInputChange(event);
  };

  // Handle dollar input changes
  const handleDollarChange = (name) => (value) => {
    const event = {
      target: {
        name: `lender_defaults.${name}`,
        value: value
      }
    };
    handleInputChange(event);
  };

  // Handle percentage input changes
  const handlePercentageChange = (name) => (value) => {
    const event = {
      target: {
        name: `lender_defaults.${name}`,
        value: value
      }
    };
    handleInputChange(event);
  };

  // Frequency options
  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi_weekly', label: 'Bi-Weekly' },
    { value: 'semi_monthly', label: 'Semi-Monthly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  // Yes/No options
  const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];

  // Method options
  const methodOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'auto', label: 'Auto' }
  ];

  // Recourse options
  const recourseOptions = [
    { value: 'none', label: 'None' },
    { value: 'full', label: 'Full' },
    { value: 'partial', label: 'Partial' }
  ];

  // State options (можно добавить больше при необходимости)
  const stateOptions = [
    { value: 'MI', label: 'MI' },
    { value: 'NY', label: 'NY' },
    { value: 'CA', label: 'CA' },
    { value: 'TX', label: 'TX' }
  ];

  // Helper to find selected option
  const getSelectedOption = (options, value) => {
    if (!value) return null;
    return options.find(opt => opt.value === value);
  };

  return (
    <div >
      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Loans Booked To This Portfolio:</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              placeholder="Select Portfolio"
              name="loans_booked_portfolio"
              value={selectedPortfolio}
              onChange={handlePortfolioChange}
              options={portfolioOptions}
              isLoading={isLoading}
            />
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Default Pmt Frequency:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select Frequency"
                  value={getSelectedOption(frequencyOptions, lenderData.default_pmt_frequency || 'monthly')}
                  onChange={handleSelectChange('default_pmt_frequency')}
                  options={frequencyOptions}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Default Per to 1st Pmt:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select Period"
                  value={getSelectedOption(frequencyOptions, lenderData.default_per_to_1st_pmt || 'monthly')}
                  onChange={handleSelectChange('default_per_to_1st_pmt')}
                  options={frequencyOptions}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Default Num Pers To 1st Pmt:</Label>
                <Input type="number" className="form-control" style={inputStyle} name="default_num_pers_to_1st_pmt" value={lenderData.default_num_pers_to_1st_pmt || ''} onChange={handleLenderInputChange} placeholder="1" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Default Num Of Pmts:</Label>
                <Input type="number" className="form-control" style={inputStyle} name="default_num_of_pmts" value={lenderData.default_num_of_pmts || ''} onChange={handleLenderInputChange} placeholder="66" />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Default Max Loan Rate:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select"
                  value={getSelectedOption(yesNoOptions, lenderData.default_max_loan_rate || 'no')}
                  onChange={handleSelectChange('default_max_loan_rate')}
                  options={yesNoOptions}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Default Loan Rate:</Label>
                <PercentageInput
                  name="default_loan_rate"
                  value={lenderData.default_loan_rate || ''}
                  onChange={handlePercentageChange('default_loan_rate')}
                  placeholder="18.0000%"
                  decimals={4}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>Max Num Def Downs:</Label>
                <Input type="number" className="form-control" style={inputStyle} name="max_num_def_downs" value={lenderData.max_num_def_downs || ''} onChange={handleLenderInputChange} placeholder="1" />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>Require Insurance:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select"
                  value={getSelectedOption(yesNoOptions, lenderData.require_insurance || 'yes')}
                  onChange={handleSelectChange('require_insurance')}
                  options={yesNoOptions}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Late Fee Days:</Label>
                <Input
                  type="number"
                  className="form-control"
                  style={inputStyle}
                  name="late_fee_days"
                  value={lenderData.late_fee_days || ''}
                  onChange={handleLenderInputChange}
                  placeholder="0"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Late Fee Percentage:</Label>
                <PercentageInput
                  name="late_fee_percentage"
                  value={lenderData.late_fee_percentage || ''}
                  onChange={handlePercentageChange('late_fee_percentage')}
                  placeholder="0.0000%"
                  decimals={4}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Max Cmp Deductible:</Label>
                <DollarInput
                  className="form-control"
                  style={inputStyle}
                  name="max_cmp_deductible"
                  value={lenderData.max_cmp_deductible || ''}
                  onChange={handleDollarChange('max_cmp_deductible')}
                  placeholder="$500.00"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Max Coll Deductible:</Label>
                <DollarInput
                  className="form-control"
                  style={inputStyle}
                  name="max_coll_deductible"
                  value={lenderData.max_coll_deductible || ''}
                  onChange={handleDollarChange('max_coll_deductible')}
                  placeholder="$500.00"
                />
              </FormGroup>
            </Col>
          </Row>
        </Col>

        <Col md="6">
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Loans Are Serviced In-House:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select"
                  value={getSelectedOption(yesNoOptions, lenderData.loans_serviced_in_house || 'no')}
                  onChange={handleSelectChange('loans_serviced_in_house')}
                  options={yesNoOptions}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Loan Decision Method:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select Method"
                  value={getSelectedOption(methodOptions, lenderData.loan_decision_method || 'manual')}
                  onChange={handleSelectChange('loan_decision_method')}
                  options={methodOptions}
                />
              </FormGroup>
            </Col>
          </Row>

          <div >
            <h5 style={sectionHeadingStyle}>LIENHOLDER INFORMATION</h5>
            <Row>
              <Col md="6"><FormGroup><Label>Officer:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_officer" value={lenderData.lienholder_officer || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label>Lien Holder #:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_number" value={lenderData.lienholder_number || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
            </Row>
            <Row>
              <Col md="6"><FormGroup><Label>Lien Holder Name:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_name" value={lenderData.lienholder_name || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
              <Col md="3"><FormGroup><Label>Phone:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_phone" value={lenderData.lienholder_phone || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
              <Col md="3"><FormGroup><Label>Fax:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_fax" value={lenderData.lienholder_fax || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
            </Row>
            <Row>
              <Col md="6"><FormGroup><Label>Address 1:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_address_1" value={lenderData.lienholder_address_1 || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
              <Col md="6"><FormGroup><Label>Address 2:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_address_2" value={lenderData.lienholder_address_2 || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
            </Row>
            <Row>
              <Col md="4"><FormGroup><Label>City:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_city" value={lenderData.lienholder_city || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
              <Col md="4">
                <FormGroup>
                  <Label>State:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select State"
                    value={getSelectedOption(stateOptions, lenderData.lienholder_state)}
                    onChange={handleSelectChange('lienholder_state')}
                    options={stateOptions}
                  />
                </FormGroup>
              </Col>
              <Col md="4"><FormGroup><Label>County:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_county" value={lenderData.lienholder_county || ''} onChange={handleLenderInputChange} /></FormGroup></Col>
            </Row>
            <Row><Col md="12"><FormGroup><Label>Zip:</Label><Input type="text" className="form-control" style={inputStyle} name="lienholder_zip" value={lenderData.lienholder_zip || ''} onChange={handleLenderInputChange} /></FormGroup></Col></Row>
          </div>

          <div >
            <h5 style={sectionHeadingStyle}>LOSS PAYEE INFORMATION</h5>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Same As Lienholder:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select"
                    value={getSelectedOption(yesNoOptions, lenderData.loss_payee_same_as_lienholder || 'yes')}
                    onChange={handleSelectChange('loss_payee_same_as_lienholder')}
                    options={yesNoOptions}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div >
            <h5 style={sectionHeadingStyle}>DOCUMENT DEFAULTS</h5>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Side Note Document:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Document"
                    value={getSelectedOption([], lenderData.side_note_document)}
                    onChange={handleSelectChange('side_note_document')}
                    options={[]}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>CPI Document:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Document"
                    value={getSelectedOption([], lenderData.cpi_document)}
                    onChange={handleSelectChange('cpi_document')}
                    options={[]}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Arbitration:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select"
                    value={getSelectedOption(yesNoOptions, lenderData.arbitration || 'no')}
                    onChange={handleSelectChange('arbitration')}
                    options={yesNoOptions}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Recourse:</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Recourse"
                    value={getSelectedOption(recourseOptions, lenderData.recourse || 'none')}
                    onChange={handleSelectChange('recourse')}
                    options={recourseOptions}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Lenders;
